import type { IAccount } from "@/modules/account/common/types";
import { useGetBudgetTransferTemplateQuery } from "@/modules/template/services";
import {
  BudgetTransferKeys,
  PartyType,
  ResidenceType,
  TransactionTypes,
  TransferKeys,
} from "@/modules/transaction/common/enum";
import type {
  IBudgetTransferForm,
  IBudgetTransferRequest,
} from "@/modules/transaction/common/types";
import {
  RegionCodeSelectOption
} from "@/modules/transaction/components/BudgetTransfer/RegionCodeSelectOption";
import { SaveAsTemplate } from "@/modules/transaction/components/SaveAsTemplate";
import {
  budgetTransferAdditionalFields,
  getDocumentTypeOptions,
  getPartyTypeOptions,
  getResidencyOptions,
} from "@/modules/transaction/helpers";
import { useBudgetTransfer } from "@/modules/transaction/hooks";
import {
  useCreateBudgetTransferMutation,
  useGetBudgetTransferQuery,
  useGetUrgentAvailabilityQuery,
  useLazyGetBudgetTransferCommissionQuery,
  useUpdateBudgetTransferMutation,
} from "@/modules/transaction/services";
import { CURRENCY, DATE_FORMAT, INPUT_MAX_LIMIT } from "@/shared/common/constants";
import { SearchParamKeys } from "@/shared/common/enums";
import type { IExtendedObject } from "@/shared/common/types";
import { OwnAccountSelectOption, Select, TransferLayout } from "@/shared/components";
import {
  clampToCurrentDate,
  cleanupAmount,
  formatDate,
  isValidAccount,
  isValidAmount,
  mergeByKeys,
  omitKey
} from "@/shared/helpers";
import { useHasFormChanges } from "@/shared/hooks";
import { RadioGroup } from "@ab.uitools/ui-library";
import { Checkbox } from "@ab.uitools/ui-library/components/Checkbox";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { SimpleDatePicker } from "@ab.uitools/ui-library/components/DatePicker";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import type { SingleValue, TOption } from "@ab.uitools/ui-library/components/ReactSelect/types";
import { Switcher } from "@ab.uitools/ui-library/components/Switcher";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Textarea } from "@ab.uitools/ui-library/components/Textarea";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import uuid4 from "uuid4";

interface Props {
  isEdit?: boolean;
  isCopy?: boolean;
  accountsList: IAccount[];
  setIsTinValid: Dispatch<SetStateAction<boolean>>;
  setIsAccountNumberInvalid: Dispatch<SetStateAction<boolean>>;
  isNameUnique: boolean;
  setIsNameUnique: Dispatch<SetStateAction<boolean>>;
}

export const BudgetTransferForm = ({
  isEdit,
  isCopy,
  accountsList,
  setIsTinValid,
  setIsAccountNumberInvalid,
  isNameUnique,
  setIsNameUnique,
}: Props) => {
  const { t, i18n: { language } } = useTranslation();
  const { operationId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get(SearchParamKeys.TemplateId);

  const idempotencyKey = useRef(uuid4()).current;
  const [defaultData, setDefaultData] = useState<IExtendedObject>({});

  const [getCommission] = useLazyGetBudgetTransferCommissionQuery();
  const {
    data: budgetTransfer,
    isLoading: isGetTransferLoading,
  } = useGetBudgetTransferQuery({
    id: operationId
  }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !operationId
  });

  const {
    data: template,
    isLoading: isGetTemplateLoading,
  } = useGetBudgetTransferTemplateQuery({
    id: templateId as string,
  }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !templateId
  });

  const { data: isUrgentAvailable = false } = useGetUrgentAvailabilityQuery({}, {
    refetchOnMountOrArgChange: true,
  });
  const [createBudgetTransferApi, { isLoading: isCreateTransferLoading }] = useCreateBudgetTransferMutation();
  const [updateBudgetTransferApi, { isLoading: isUpdateTransferLoading }] = useUpdateBudgetTransferMutation();

  const {
    watch,
    reset,
    values,
    errors = {},
    userTin,
    setValue,
    taxPayers,
    isTaxAccount,
    fieldChanges,
    budgetAccount,
    setBudgetAccount,
    canGetCommission,
    handleClickTarget,
    regionCodeOptions,
    prevCreditAccountRef,
    accountsFilteredList,
    getBudgetAccountInfo,
    handleChangePartType,
    handleGetRegionCodeInfo,
    handleClickAuthorizedTransfer,
  } = useBudgetTransfer({
    setIsTinValid,
    accountsList,
    setIsAccountNumberInvalid,
  });

  const setDefaultValues = () => {
    const data = {
      creditBudgetAccount: budgetTransfer?.creditBudgetAccount,
      debitAccount: budgetTransfer?.debitAccount.number,
      isAuthorizedTransfer: !!budgetTransfer?.isAuthorizedTransfer,
      authorizedBudgetTransferParty: budgetTransfer?.isAuthorizedTransfer ? {
        fullName: budgetTransfer?.authorizedBudgetTransferParty?.fullName,
        residency: budgetTransfer?.authorizedBudgetTransferParty?.residency,
        partyType: budgetTransfer?.authorizedBudgetTransferParty?.partyType,
        tin: budgetTransfer?.authorizedBudgetTransferParty?.tin,
        ssn: budgetTransfer?.authorizedBudgetTransferParty?.ssn,
        documentType: budgetTransfer?.authorizedBudgetTransferParty?.documentType,
        documentNumber: budgetTransfer?.authorizedBudgetTransferParty?.documentNumber,
        address: budgetTransfer?.authorizedBudgetTransferParty?.address,
        additionalInfo: budgetTransfer?.authorizedBudgetTransferParty?.additionalInfo,
        authorizedPerson: budgetTransfer?.authorizedBudgetTransferParty?.authorizedPerson,
      } : null,
      transactionNumber: isEdit ? budgetTransfer?.transactionNumber : values.transactionNumber,
      beneficiary: budgetTransfer?.beneficiary,
      scheduleDate: clampToCurrentDate(budgetTransfer?.scheduleDate),
      regionCode: budgetTransfer?.regionCode,
      amount: budgetTransfer?.amount,
      tin: budgetTransfer?.tin,
      fee: budgetTransfer?.commissionAmount || 0,
      purpose: budgetTransfer?.purpose,
      isUrgent: isUrgentAvailable && !isCopy ? budgetTransfer?.isUrgent : false,
      currency: budgetTransfer?.currency,
      commissionAccount: budgetTransfer?.commissionAccount?.number,
      messages: {
        reportCode: budgetTransfer?.messages?.reportCode || "",
        message1: budgetTransfer?.messages?.message1 || "",
        message2: budgetTransfer?.messages?.message2 || "",
        message3: budgetTransfer?.messages?.message3 || "",
        message4: budgetTransfer?.messages?.message4 || "",
        message5: budgetTransfer?.messages?.message5 || "",
        message6: budgetTransfer?.messages?.message6 || "",
      }
    };
    reset?.(data);
    setDefaultData({
      ...data,
      scheduleDate: new Date(budgetTransfer?.scheduleDate as string),
    });
  };

  const currentData = useMemo(() => ({
    creditBudgetAccount: values.creditBudgetAccount,
    debitAccount: values.debitAccount,
    isAuthorizedTransfer: !!values?.isAuthorizedTransfer,
    authorizedBudgetTransferParty: values.isAuthorizedTransfer ? {
      fullName: values?.authorizedBudgetTransferParty?.fullName,
      residency: values?.authorizedBudgetTransferParty?.residency,
      partyType: values?.authorizedBudgetTransferParty?.partyType,
      tin: values?.authorizedBudgetTransferParty?.tin,
      ssn: values?.authorizedBudgetTransferParty?.ssn,
      documentType: values?.authorizedBudgetTransferParty?.documentType,
      documentNumber: values?.authorizedBudgetTransferParty?.documentNumber,
      address: values?.authorizedBudgetTransferParty?.address,
      additionalInfo: values?.authorizedBudgetTransferParty?.additionalInfo,
      authorizedPerson: values?.authorizedBudgetTransferParty?.authorizedPerson,
    } : null,
    transactionNumber: values.transactionNumber,
    beneficiary: values.beneficiary,
    scheduleDate: new Date(values.scheduleDate),
    amount: +cleanupAmount(values.amount),
    tin: values.tin,
    fee: values.fee,
    purpose: values.purpose,
    isUrgent: values.isUrgent,
    currency: values.currency,
    commissionAccount: values.commissionAccount,
    regionCode: values?.regionCode,
    messages: {
      reportCode: values?.messages?.reportCode,
      message1: values?.messages?.message1,
      message2: values?.messages?.message2,
      message3: values?.messages?.message3,
      message4: values?.messages?.message4,
      message5: values?.messages?.message5,
      message6: values?.messages?.message6,
    }
  }), [values]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  const isUrgent = useMemo(() => {
    return isUrgentAvailable &&
      values.creditBudgetAccount &&
      values.debitAccount;
  }, [
    isUrgentAvailable,
    values.creditBudgetAccount,
    values.debitAccount,
  ]);

  const handleChangeUrgent = (event: boolean) => {
    if (event) {
      setValue(TransferKeys.ScheduleDate, new Date());
    }
  };

  const budgetTransferAction = useCallback((values: IBudgetTransferForm) => {
    const requestData: IBudgetTransferRequest = omitKey({
      ...values,
      scheduleDate: formatDate(values.scheduleDate, DATE_FORMAT.YYYY_MM_DD),
      amount: +cleanupAmount(`${values.amount}`),
      authorizedBudgetTransferParty: values.isAuthorizedTransfer ? values.authorizedBudgetTransferParty : null,
      debitAccount: accountsList?.find(({ number }) => number === values.debitAccount),
      commissionAccount: values.commissionAccount ? accountsList?.find(({ number }) => {
        return number === values.commissionAccount;
      }) : undefined,
    }, [TransferKeys.TemplateName, TransferKeys.IsSaveAsTemplate]);

    return isEdit ?
      updateBudgetTransferApi({
        data: requestData,
        id: `${budgetTransfer?.id}`
      }).unwrap() :
      createBudgetTransferApi({
        data: requestData,
        idempotencyKey,
      }).unwrap();
  }, [accountsList, budgetTransfer]);

  const maxAvailableDate = useMemo(() => {
    return values.isUrgent ? new Date() : undefined;
  }, [values.isUrgent]);

  const partyType = watch?.(BudgetTransferKeys.PartyType);
  const isAuthorizedTransfer = watch?.(BudgetTransferKeys.IsAuthorizedTransfer);
  const creditBudgetAccount = watch?.(BudgetTransferKeys.CreditBudgetAccount);

  useEffect(() => {
    if ((isEdit || isCopy) && !!budgetTransfer?.id) {
      setDefaultValues();
      getBudgetAccountInfo(budgetTransfer?.creditBudgetAccount);
    }
  }, [isEdit, isCopy, budgetTransfer]);

  useEffect(() => {
    if (template) {
      const data = mergeByKeys(values, {
        ...template,
        commissionAccount: template?.commissionAccount?.number,
        debitAccount: template?.debitAccount?.number,
      });
      reset?.(data);
      getBudgetAccountInfo(template?.creditBudgetAccount);
    }
  }, [template]);

  useEffect(() => {
    if (!isEdit) {
      setValue(TransferKeys.ScheduleDate, new Date());
    }
    if (!isEdit && !isCopy && !templateId) {
      setValue(TransferKeys.Purpose, t("transfer.budget.purpose"));
    }
  }, []);

  return (
    <TransferLayout
      title={t("transfer.budget.title")}
      page="Budget transfer"
      transactionType={TransactionTypes.BudgetTransfer}
      accountsList={accountsList}
      isEdit={isEdit}
      canClose={false}
      getCommission={getCommission}
      hasChanges={hasChanges}
      canGetCommission={canGetCommission}
      fieldChanges={fieldChanges}
      isSaveDisabled={!isNameUnique && values.isSaveAsTemplate}
      isSignDisabled={!isNameUnique && values.isSaveAsTemplate}
      isLoading={isCreateTransferLoading || isUpdateTransferLoading}
      isGetTransferLoading={isGetTransferLoading || isGetTemplateLoading}
      getTransferId={budgetTransferAction}
    >
      <CollapseGroup
        className="collapse-group"
        items={[
          {
            content: <>
              <FormField
                name="creditBudgetAccount"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    type="numeric"
                    allowNegative={false}
                    allowLeadingZeros={false}
                    handleChange={(_, value) => {
                      if (value !== prevCreditAccountRef.current && isValidAccount(value)) {
                        getBudgetAccountInfo(value);
                      } else {
                        getBudgetAccountInfo.cancel();
                        setBudgetAccount(null);
                        setValue?.(BudgetTransferKeys.RegionCode, "");
                        prevCreditAccountRef.current = "";
                      }
                    }}
                    label={t("transfer.receiverAccount")}
                    placeholder={t("transfer.enterReceiverAccount")}
                  />
                )}
              />
              {budgetAccount?.description && (
                <div className="mt-20">
                  <Text weight="semibold">
                    {t("transfer.beneficiaryName")}
                  </Text>
                  <Text className="mb-32 mt-8">
                    {budgetAccount.description}
                  </Text>
                </div>
              )}
              <FormField
                name="isAuthorizedTransfer"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Checkbox
                    {...props}
                    onClick={handleClickAuthorizedTransfer}
                    label={t("transfer.budget.makeTransferForAnother")}
                  />
                )}
              />
              {!isAuthorizedTransfer && (
                <>
                  {isTaxAccount && values.regionCode && (
                    <div className="mt-20">
                      <Text weight="semibold">{t("transfer.regionalCode")}</Text>
                      <Text className="mb-32 mt-8">
                        {taxPayers?.regionCode} {taxPayers?.regionName}
                      </Text>
                    </div>
                  )}
                  {!!regionCodeOptions.length &&
                    creditBudgetAccount &&
                    !isTaxAccount && (
                      <FormField
                        name="regionCode"
                        className="mb-32"
                        shouldValidate
                        errorMessageIcon="infoFilled"
                        As={(props) => (
                          <Select
                            {...props}
                            required
                            options={regionCodeOptions}
                            label={t("transfer.regionalCode")}
                            placeholder={t("transfer.chooseReginalCode")}
                            selectedValue={values.regionCode}
                            components={{ Option: RegionCodeSelectOption }}
                          />
                        )}
                      />
                    )}
                </>
              )}
              {isAuthorizedTransfer && (
                <>
                  <FormField
                    name="authorizedBudgetTransferParty.fullName"
                    className="mb-32 mt-16"
                    errorMessageIcon="infoFilled"
                    As={(props) => (
                      <Input
                        {...props}
                        required
                        label={t("transfer.budgetTransfer.fullName")}
                        placeholder={t("transfer.budgetTransfer.enterFullName")}
                      />
                    )}
                  />
                  <FormField
                    name="authorizedBudgetTransferParty.residency"
                    className="mb-32 mt-16"
                    errorMessageIcon="infoFilled"
                    As={(props) => (
                      <RadioGroup
                        {...props}
                        label={t("transfer.budgetTransfer.thirdPartyResidence")}
                        selected={values.authorizedBudgetTransferParty?.residency}
                        options={getResidencyOptions({ t })}
                      />
                    )}
                  />
                  <FormField
                    name="authorizedBudgetTransferParty.partyType"
                    className="mb-32"
                    errorMessageIcon="infoFilled"
                    shouldValidate
                    As={(props) => (
                      <Select
                        {...props}
                        required
                        options={getPartyTypeOptions({ t })}
                        handleChange={(value) => handleChangePartType(value as SingleValue<TOption>)}
                        label={t("transfer.budgetTransfer.legalStatus")}
                        placeholder={t("transfer.budgetTransfer.chooseLegalStatus")}
                      />
                    )}
                  />
                  {partyType === PartyType.NaturalPerson && (
                    <FormField
                      name="authorizedBudgetTransferParty.ssn"
                      className="mb-32"
                      key="authorizedBudgetTransferParty.ssn"
                      errorMessageIcon="infoFilled"
                      As={(props) => (
                        <Input
                          {...props}
                          required
                          label={t("transfer.ssn")}
                          placeholder={t("transfer.budgetTransfer.ssn")}
                        />
                      )}
                    />
                  )}
                  {(partyType === PartyType.LegalEntity ||
                    partyType === PartyType.PrivateEntrepreneur) && (
                    <FormField
                      name="authorizedBudgetTransferParty.tin"
                      className="mb-32"
                      key="authorizedBudgetTransferParty.tin"
                      errorMessageIcon="infoFilled"
                      As={(props) => (
                        <Input
                          {...props}
                          required
                          onChange={handleGetRegionCodeInfo}
                          label={t("transfer.tinCode")}
                          placeholder={t("transfer.budgetTransfer.enterTinCode")}
                        />
                      )}
                    />
                  )}
                  <FormField
                    name="authorizedBudgetTransferParty.documentType"
                    className="mb-32"
                    shouldValidate
                    errorMessageIcon="infoFilled"
                    As={(props) => (
                      <Select
                        {...props}
                        isClearable
                        setSelectedValue={(value) => {
                          if (!value) {
                            setValue(BudgetTransferKeys.DocumentType, "");
                            setValue(BudgetTransferKeys.DocumentNumber, "");
                          }
                        }}
                        required={
                          values?.authorizedBudgetTransferParty?.residency === ResidenceType.NonResident &&
                          values?.authorizedBudgetTransferParty.partyType === PartyType.NaturalPerson
                        }
                        options={getDocumentTypeOptions({ t })}
                        label={t("transfer.budgetTransfer.passportType")}
                        placeholder={t("transfer.budgetTransfer.choosePassportType")}
                      />
                    )}
                  />
                  {!!values?.authorizedBudgetTransferParty?.documentType && (
                    <FormField
                      name="authorizedBudgetTransferParty.documentNumber"
                      className="mb-32"
                      errorMessageIcon="infoFilled"
                      As={(props) => (
                        <Input
                          {...props}
                          required={!!values?.authorizedBudgetTransferParty?.documentType}
                          label={t("transfer.budgetTransfer.documentData")}
                          placeholder={t("transfer.budgetTransfer.enterDocumentData")}
                        />
                      )}
                    />
                  )}
                  <FormField
                    name="authorizedBudgetTransferParty.address"
                    className="mb-32"
                    errorMessageIcon="infoFilled"
                    As={(props) => (
                      <Input
                        {...props}
                        label={t("transfer.budgetTransfer.address")}
                        placeholder={t("transfer.budgetTransfer.enterAddress")}
                      />
                    )}
                  />
                  <FormField
                    name="authorizedBudgetTransferParty.authorizedPerson"
                    className="mb-32"
                    errorMessageIcon="infoFilled"
                    As={(props) => (
                      <Input
                        {...props}
                        label={t("transfer.budgetTransfer.authorizedPerson")}
                        placeholder={t("transfer.budgetTransfer.enterAuthorizedPerson")}
                      />
                    )}
                  />
                  <FormField
                    name="authorizedBudgetTransferParty.additionalInfo"
                    className="mb-32"
                    errorMessageIcon="infoFilled"
                    As={(props) => (
                      <Input
                        {...props}
                        label={t("transfer.budgetTransfer.otherInformation")}
                        placeholder={t("transfer.budgetTransfer.enterOtherInformation")}
                      />
                    )}
                  />
                </>
              )}
              {isAuthorizedTransfer && (
                <>
                  {isTaxAccount && values.regionCode && (
                    partyType === PartyType.LegalEntity ||
                    partyType === PartyType.PrivateEntrepreneur
                  ) && (
                    <div className="mt-20">
                      <Text weight="semibold">{t("transfer.regionalCode")}</Text>
                      <Text className="mb-32 mt-8">
                        {taxPayers?.regionCode} {taxPayers?.regionName}
                      </Text>
                    </div>
                  )}
                  {!!regionCodeOptions.length && !!partyType &&
                    creditBudgetAccount &&
                    ((isTaxAccount && partyType === PartyType.NaturalPerson) || !isTaxAccount) && (
                      <FormField
                        name="regionCode"
                        className="mb-32"
                        shouldValidate
                        errorMessageIcon="infoFilled"
                        As={(props) => (
                          <Select
                            {...props}
                            required
                            options={regionCodeOptions}
                            label={t("transfer.regionalCode")}
                            placeholder={t("transfer.chooseReginalCode")}
                            selectedValue={values.regionCode}
                            components={{ Option: RegionCodeSelectOption }}
                          />
                        )}
                      />
                    )}
                </>
              )}
              <FormField
                name="amount"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    type="numeric"
                    allowNegative={false}
                    allowLeadingZeros={false}
                    label={t("general.amount")}
                    placeholder={t("general.enterAmount")}
                    thousandSeparator=","
                    decimalScale={values.currency === CURRENCY.AMD.label ? 1 : 2}
                    rightIconProps={{
                      Component: CURRENCY[values.currency]?.symbolIcon || "",
                    }}
                    isAllowed={({ formattedValue }) => isValidAmount(formattedValue)}
                  />
                )}
              />
              <FormField
                name="debitAccount"
                className="mb-32"
                shouldValidate
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Select
                    {...props}
                    required
                    isSearchable
                    className="custom-select"
                    options={accountsFilteredList || []}
                    label={t("transfer.debitAccount")}
                    placeholder={t("transfer.chooseDebitAccount")}
                    handleChange={(value) => handleClickTarget(value as SingleValue<TOption>)}
                    components={{ Option: OwnAccountSelectOption }}
                  />
                )}
              />
              <FormField
                name="purpose"
                className="mb-32"
                errorMessageIcon="infoFilled"
                hideErrorMessage
                As={(props) => (
                  <Textarea
                    {...props}
                    required
                    error={`${errors?.purpose?.message || ""}`}
                    label={t("general.purpose")}
                    placeHolder={t("general.enterYourPurpose")}
                    maxCount={INPUT_MAX_LIMIT}
                  />
                )}
              />
              <Divider isHorizontal type="primary" color="light"/>
              <div className="flexbox justify-content--between mt-16">
                <div className="flex-direction--column">
                  <Text type="primary" weight="bolder">
                    {t("transfer.urgentTransfer")}
                  </Text>
                  <Text type="secondary">
                    {t("transfer.urgentTransferDescription")}
                  </Text>
                </div>
                <FormField
                  name="isUrgent"
                  errorMessageIcon="infoFilled"
                  shouldValidate
                  As={(props) => (
                    <Switcher {...props} disabled={!isUrgent} onClick={handleChangeUrgent}/>
                  )}
                />
              </div>
              <SaveAsTemplate
                setValue={setValue}
                setIsNameUnique={setIsNameUnique}
                isSaveAsTemplate={values.isSaveAsTemplate}
              />
            </>,
            title: t("transfer.transferDetails"),
            value: "transfer_details",
            isOpen: true,
          },
          {
            content: <>
              <div className="mt-20">
                <Text weight="semibold">{t("transfer.tin")}</Text>
                <Text className="mb-32 mt-8">
                  {userTin}
                </Text>
              </div>
              <FormField
                name="scheduleDate"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <SimpleDatePicker
                    {...props}
                    required
                    closeOnScroll
                    locale={language}
                    format={DATE_FORMAT.DD_MM_YYYY}
                    label={t("general.date")}
                    value={props.value as Date}
                    minDate={new Date()}
                    maxDate={maxAvailableDate}
                  />
                )}
              />
              <FormField
                name="transactionNumber"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("transfer.transactionNumber")}
                    placeholder={t("transfer.enterTransactionNumber")}
                  />
                )}
              />
              {budgetTransferAdditionalFields.map((field) => (
                <FormField
                  key={field.name}
                  name={field.name}
                  className={field.classNames}
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <Input
                      {...props}
                      label={t(field.label)}
                      placeholder={t(field.placeholder)}
                    />
                  )}
                />
              ))}
            </>,
            title: t("general.otherDetails"),
            value: "other_details",
            isOpen: true,
          },
        ]}/>
    </TransferLayout>
  );
};
